import { useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, FolderOpen, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { trpc } from "@/lib/trpc";
import { useOptionalProject } from "@/components/project-context";

function projectActivityLabel(project: {
  walkthroughCount?: number;
  openFindingCount?: number;
  lastWalkthroughAt?: string | null;
}): string | null {
  const parts: string[] = [];
  const wc = project.walkthroughCount ?? 0;
  const fc = project.openFindingCount ?? 0;
  if (wc > 0) parts.push(`${wc} walkthrough${wc !== 1 ? "s" : ""}`);
  if (fc > 0) parts.push(`${fc} open finding${fc !== 1 ? "s" : ""}`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function getTransferableSubPath(
  pathname: string,
  currentProjectId: string,
): string {
  const prefix = `/projects/${currentProjectId}/`;
  if (!pathname.startsWith(prefix)) return "";
  const subPath = pathname.slice(prefix.length);

  const transferable = [
    "sources",
    "sources/discover",
    "scenarios",
    "personas",
    "references",
    "findings",
    "live-walkthrough",
    "context",
    "understanding",
    "terminology",
    "walkthroughs",
  ];
  if (transferable.includes(subPath)) return subPath;

  return "";
}

export function ProjectSwitcher({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const activeProject = useOptionalProject();
  const { data: projects, isLoading } = trpc.project.list.useQuery();

  const initial = activeProject?.name?.[0]?.toUpperCase() ?? "N";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex w-full items-center gap-2 rounded-[7px] border border-sidebar-border bg-sidebar-accent/40 px-2.5 py-[7px] text-left transition-colors hover:bg-sidebar-accent hover:border-sidebar-border"
          aria-label={`Switch project — current: ${activeProject?.name ?? "none"}`}
        >
          <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[5px] bg-[var(--primary)] text-xs font-bold text-primary-foreground">
            {initial}
          </span>
          <span className="flex-1 truncate text-sm font-medium text-sidebar-foreground">
            {isLoading
              ? "Loading..."
              : (activeProject?.name ?? "Select project")}
          </span>
          <ChevronsUpDown
            className="h-3 w-3 shrink-0 text-sidebar-muted-foreground"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        <div className="max-h-56 overflow-y-auto">
          {projects?.map((project) => {
            const meta = projectActivityLabel(project);
            return (
              <DropdownMenuItem
                key={project.id}
                onClick={() => {
                  const subPath = activeProject
                    ? getTransferableSubPath(
                        location.pathname,
                        activeProject.id,
                      )
                    : "";
                  const target = subPath
                    ? `/projects/${project.id}/${subPath}`
                    : `/projects/${project.id}`;
                  navigate(target);
                  onNavigate?.();
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-3.5 w-3.5 shrink-0",
                    activeProject?.id === project.id
                      ? "opacity-100"
                      : "opacity-0",
                  )}
                />
                <div className="flex-1 min-w-0">
                  <span className="block truncate">{project.name}</span>
                  {meta && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {meta}
                    </span>
                  )}
                </div>
              </DropdownMenuItem>
            );
          })}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            navigate("/projects");
            onNavigate?.();
          }}
        >
          <FolderOpen className="mr-2 h-3.5 w-3.5" />
          All projects
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            navigate("/projects/new");
            onNavigate?.();
          }}
        >
          <Plus className="mr-2 h-3.5 w-3.5" />
          New Project
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
